import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DeviceService } from '@omni/services/device/device.service';
import { GoAppNavigationService } from '@omni/services/go-app-navigation.service';
import { EventsService } from '@omni/services/events/events.service';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { ActivityService } from '@omni/services/activity/activity.service';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { IActivityEvent } from '@omni/interfaces/activity/activity-event.interface';
import { Subject } from 'rxjs';
import { composeFooterNavigation, isFooterToolOption } from './footer-navigation-options';

@Component({
  selector: 'go-app-footer-navigation-menu',
  templateUrl: './go-app-footer-navigation-menu.component.html',
  styleUrl: './go-app-footer-navigation-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GoAppFooterNavigationMenuComponent {

  @Output() menuOptionClicked: EventEmitter<any> = new EventEmitter<any>();

  public isMobileView: boolean = false;
  public isFooterExpanded: boolean = false;
  public footerOptions = [];
  public moreOptions = [];
  /** Bar destinations placed before the tools promoted from the tool menu. */
  private leadingFooterOptions = [];
  /** Bar destinations placed after the tools promoted from the tool menu. */
  private trailingFooterOptions = [];
  private prevSelectedOption = null;
  // public selectedView = null;

  @ViewChild('menuContainer') menuContainerRef: ElementRef;
  @ViewChild('overlay') overlayRef: ElementRef;
  @ViewChild('footer') footerRef: ElementRef;

  private _selectedView: string;

  // @Input()
  // set selectedView(value: string) {
  //   if (this.selectedView !== value) {
  //     this._selectedView = value;
  //     this.onClickMenuOption(value);
  //   }
  // }

  get selectedView(): string {
    return this._selectedView;
  }
  private readonly destroy$ = new Subject<boolean>();

  @Output() onOptionSelection = new EventEmitter<string>();

  constructor(private readonly translate: TranslateService,
    public readonly device: DeviceService,
    private readonly goAppNavigationService: GoAppNavigationService,
    private readonly events: EventsService,
    private readonly navService: NavigationService,
    private readonly activityService: ActivityService,
    private readonly cd: ChangeDetectorRef,
  ) {

  }

  ngOnInit() {
    this.initMobileFooterOptions();
    this.initFooterToolOptions();

    this.events.subscribe('clearSelectedView', () => {
      this.footerOptions.forEach(op => op.selected = false);
      this._selectedView = undefined;
    });

    this.events.subscribe('navigateTo', (id, navigation?: { opened: boolean }) => {
      const option = this.footerOptions.find(op => op.id === id) ?? this.moreOptions.find(op => op.id === id);
      if (option) {
        this.onClickMenuOption(option);
        if (navigation) navigation.opened = true;
      }
    });

    this.events.subscribe('setSelectedNavView', (view: string) => {
      this._selectedView = undefined;
      this.footerOptions.forEach(op => op.selected = isFooterToolOption(op.id) && op.id === view);
      this.moreOptions.forEach(op => op.selected = op.id === view);
    });

    this.events.observe('footer:selectedViewChanged')
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedView: 'dashboard' | 'calendar' | 'more') => {
        this.footerOptions.forEach(op => op.selected = op.id == selectedView);
        this.moreOptions.forEach(op => op.selected = false);
        this._selectedView = selectedView;
        this.cd.detectChanges();
      })
    
    this.activityService.activityEvent$ .pipe(
          debounceTime(250),
        ) .subscribe((event: IActivityEvent) => {
          if (event?.activity && event.action === 'Create' && this.navService.getCurrentPageName() == PageName.GoAppActivitiesPageComponent) {
            this.onClickMenuOption({ id: 'calendar' });
          }
        });
  }

  private initMobileFooterOptions() {
    this.leadingFooterOptions = [{
      id: 'dashboard',
      visible: true,
      iconPath: 'assets/imgs/home-grey.svg',
      selectedIconPath: 'assets/imgs/home-blue.svg',
      displayName: this.translate.instant('HOME'),
      selected: true,
      component: 'HomePage',
      enable: true
    },
    {
      id: 'calendar',
      visible: true,
      iconPath: 'assets/imgs/calendar-grey.svg',
      selectedIconPath: 'assets/imgs/calendar-blue.svg',
      displayName: this.translate.instant('CALENDAR'),
      selected: false,
      component: 'CalendarPage',
      enable: true
    }];
    this.trailingFooterOptions = [{
      id: 'more',
      visible: true,
      iconPath: 'assets/imgs/more-grey-icon.svg',
      selectedIconPath: 'assets/imgs/more-blue-icon.svg',
      displayName: this.translate.instant('MORE'),
      selected: false,
      component: '',
      enable: true
    }];
    this.footerOptions = [...this.leadingFooterOptions, ...this.trailingFooterOptions];
  }

  /**
   * Keeps the bar tools and the More list in step with the tool menu. Contacts
   * and Accounts appear in the bar only when the tool menu grants them, and
   * they are removed from the More list. The current selection is carried over
   * to the refreshed tool options.
   */
  private initFooterToolOptions() {
    this.goAppNavigationService.menuOptions
      .pipe(takeUntil(this.destroy$))
      .subscribe(options => {
        const selectedIds = new Set([...this.footerOptions, ...this.moreOptions].filter(op => op.selected).map(op => op.id));
        const navigation = composeFooterNavigation(this.leadingFooterOptions, this.trailingFooterOptions, options);
        this.footerOptions = navigation.barOptions;
        this.moreOptions = navigation.moreOptions;
        [...this.footerOptions, ...this.moreOptions].forEach(op => op.selected = selectedIds.has(op.id));
        this.cd.markForCheck();
      });
  }

  onClickMenuOption(option) {
    if(this.navService.getCurrentMasterPageName() === PageName.EventsToolPageComponent && option.id === 'eventstool') return;
    if (this.isFooterExpanded && option.id == "more" && this.prevSelectedOption) {
      this.footerOptions.forEach(op => op.selected = op.id == this.prevSelectedOption.id);
    }
    if (option.id != "more" && this.isFooterExpanded) {
      this.isFooterExpanded = false;
    } 
    switch (option.id) {
      case "hamburger":
        break;
      case "dashboard":
      case "calendar":
        if (this.navService.getCurrentMasterPageName() !== PageName.GoAppActivitiesPageComponent) {
          this.navService.setRootWithTracking(PageName.GoAppActivitiesPageComponent, PageName.GoAppActivitiesPageComponent, { __source: 'dashboard' }, PageName.GoAppActivitiesPageComponent);
        } 
        setTimeout(() => {
          this.activityService.activitiesSearchText = '';
          this.footerOptions.forEach(op => op.selected = option.id == op.id);
          this.events.publish('footer:selectedViewChanged', option.id);
          this.moreOptions.forEach(op => op.selected = false);
          this.events.publish('selected-nav-view', option.id);
          this.cd.markForCheck();
        }, 0);
        break;
      case "more":
        if (!this.isFooterExpanded)
          this.prevSelectedOption = this.footerOptions.find(op => op.selected);
        this.isFooterExpanded = true;
        this.footerOptions.forEach(op => op.selected = option.id == op.id);
        break;
      default:
        this.activityService.activityOpenedFromTodo = false;
        this.footerOptions.forEach(op => op.selected = op.id === option.id);
        if (option.component !== 'HyperlinkExtURL' || option.openMode === 1) {
          this.moreOptions.forEach(op => op.selected = option.id == op.id);
        }
        this.goAppNavigationService.navigateToMenuOption(option);
        break;
    }


    const view = this.footerOptions.find(op => op.selected)?.id;
    if (this.selectedView !== view) {
      this._selectedView = view;
    }
    // this.onOptionSelection.emit(this.selectedView);
  }

  /**
   * Activates a bar destination from the keyboard in the same way as a tap.
   *
   * @param event Enter or Space key event; its default action is suppressed.
   * @param option Bar destination to activate.
   */
  onMenuOptionKeydown(event: Event, option) {
    event.preventDefault();
    this.onClickMenuOption(option);
  }

  trackByOptionId(index: number, option: any): string {
    return option?.id ?? index;
  }

  closeMoreOptions() {
    this.isFooterExpanded = false;
    if (this.prevSelectedOption?.id !== "more") {
      this.footerOptions.forEach(op => op.selected = op.id == this.prevSelectedOption.id);
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
